import { Navigate, Outlet, createBrowserRouter } from "react-router-dom";

import { PageLayout } from "@fidelix/fx-miranda";
import {
  DEVICES_BACKUP,
  DEVICES_CREATEDEVICE,
  DEVICES_CREATEEXTERNALDEVICE,
  DEVICES_EDITDEVICE,
  DEVICES_EDITEXTERNALDEVICE,
  DEVICES_OVERVIEW,
  DEVICES_VIEWDEVICE,
  LEVELS_CREATE,
  LEVELS_EDIT,
  LEVELS_VIEW,
  POINTGROUPS_CREATEGROUP,
  POINTGROUPS_EDITGROUPSETTINGS,
  POINTGROUPS_EDITPOINTS,
  POINTGROUPS_OVERVIEW,
  POINTGROUPS_VIEWGROUP,
  POINTS_LIST,
} from "../routes/route-paths";
import { RootRouteError } from "../common/root-route-error";
import { ProtectedRoute } from "../common/protected-route";
import { RootRoute } from "./root";
import {
  restoreSearchParamsLoader,
  usePersistSearchParams,
} from "../utils/url";
import { MANAGE_ROUTE } from "../manage/route";
import { DeviceStatusProvider } from "../context/device-status-context";
import { Navbar } from "../topnavi/nav-bar";

function CompanyRoute() {
  usePersistSearchParams();
  return (
    <DeviceStatusProvider>
      <PageLayout navbar={<Navbar />}>
        <Outlet />
      </PageLayout>
    </DeviceStatusProvider>
  );
}
/**
 * This is the way to define routes with React Router v6.4 and higher.
 * It enables the data APIs like loaders, actions, fetchers and more.
 * See: https://reactrouter.com/en/main/routers/create-browser-router
 */
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootRoute />,
    errorElement: <RootRouteError />,
    children: [
      /**
       * We dont' render anything in the root route `/` so we want to redirect
       * the user to the protected `/company` url segment that will handle the
       * automatic redirect to the first available company if the user is authorized.
       */
      {
        index: true,
        element: <Navigate to="company" replace />,
      },
      {
        path: "auth/callback",
        lazy: () => import("./authenticated-callback"),
      },
      {
        path: "auth/unauthorized",
        lazy: () => import("./unauthorized"),
      },
      {
        path: "logout",
        lazy: () => import("./logout"),
      },
      /**
       * All the main routes of the app are under the `/company` url segment.
       */
      {
        path: "company",
        element: <ProtectedRoute />,
        children: [
          /**
           * Redirect to the first available company if the `:companyId` param
           * is not present in the URL.
           */
          {
            index: true,
            lazy: () => import("./redirect-to-company"),
          },
          {
            path: ":companyId",
            element: <CompanyRoute />, // this wraps all child routes
            children: [
              /**
               * Redirect to `/devices` if user lands on `/company/:companyId`
               * since that is the "home" page of the app.
               */
              {
                index: true,
                element: <Navigate to={`${DEVICES_OVERVIEW}`} replace />,
              },

              // Devices

              {
                path: `${DEVICES_OVERVIEW}/:id?`,
                lazy: () => import("../devices/routes/device-list.route"),
                loader: restoreSearchParamsLoader,
              },
              {
                path: `${DEVICES_VIEWDEVICE}/:id`,
                lazy: () => import("../devices/routes/device-view.route"),
              },
              {
                path: `${DEVICES_EDITDEVICE}/:id`,
                lazy: () => import("../devices/routes/device-edit.route"),
              },
              {
                path: `${DEVICES_CREATEDEVICE}/:id`,
                lazy: () => import("../devices/routes/device-create.route"),
              },
              {
                path: `${DEVICES_CREATEEXTERNALDEVICE}/:id`,
                lazy: () =>
                  import("../devices/routes/device-external-create.route"),
              },
              {
                path: `${DEVICES_EDITEXTERNALDEVICE}/:id`,
                lazy: () =>
                  import("../devices/routes/device-external-edit.route"),
              },

              {
                path: `${DEVICES_BACKUP}/:id`,
                lazy: () => import("../devices/routes/device-backup.route"),
              },

              // Levels

              {
                path: `${LEVELS_VIEW}/:id`,
                lazy: () => import("../levels/routes/level-view.route"),
              },
              {
                path: `${LEVELS_CREATE}/:id?`,
                lazy: () => import("../levels/routes/level-create.route"),
              },
              {
                path: `${LEVELS_EDIT}/:id`,
                lazy: () => import("../levels/routes/level-edit.route"),
              },

              // Point groups

              {
                path: `${POINTGROUPS_OVERVIEW}`,
                lazy: () =>
                  import("../pointgroups/routes/point-group-overview.route"),
              },
              {
                path: `${POINTGROUPS_CREATEGROUP}`,
                lazy: () =>
                  import("../pointgroups/routes/point-group-create.route"),
              },
              {
                path: `${POINTGROUPS_VIEWGROUP}/:id`,
                lazy: () =>
                  import("../pointgroups/routes/point-group-view.route"),
              },
              {
                path: `${POINTGROUPS_EDITGROUPSETTINGS}/:id`,
                lazy: () =>
                  import(
                    "../pointgroups/routes/point-group-edit-settings.route"
                  ),
              },
              {
                path: `${POINTGROUPS_EDITPOINTS}/:id`,
                lazy: () =>
                  import("../pointgroups/routes/point-group-edit-points.route"),
              },
              // Points

              {
                path: `${POINTS_LIST}`,
                lazy: () => import("../points/points-list/points-list.route"),
              },
              /**
               * This catch-all route is rendered if none of the above routes match
               */
              {
                path: "*",
                lazy: () => import("./not-found"),
              },
            ],
          },
        ],
      },
      /**
       *  Manage section route defined under MANAGE_ROUTE
       */
      ...MANAGE_ROUTE,

      /**
       * This catch-all route is rendered if none of the above routes match
       */
      {
        path: "*",
        lazy: () => import("./not-found"),
      },
    ],
  },
]);
